import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Shield, UserCog, Key } from "lucide-react";

/* ============ STAFF MANAGEMENT ============ */
const STAFF_PERMISSIONS = [
  { id: "orders", label: "Beställningar", description: "Visa och hantera beställningar" },
  { id: "menu", label: "Meny", description: "Visa och hantera meny" },
  { id: "inventory", label: "Lager", description: "Hantera lager och saldo" },
  { id: "analytics", label: "Analys", description: "Visa försäljning och statistik" },
  { id: "settings", label: "Inställningar", description: "Ändra restauranginställningar" },
  { id: "promotions", label: "Kampanjer", description: "Hantera rabattkoder" },
  { id: "staff", label: "Personal", description: "Hantera personalkonton" },

  { id: "ai-advisor", label: "AI-rådgivare", description: "AI-insikter för verksamheten" },
  { id: "accounting", label: "Bokföring", description: "Moms och utgifter" },
  { id: "database", label: "Databas", description: "Se och hantera databasen (backup & återställning)" },
];

function PermissionGrid({ selected, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {STAFF_PERMISSIONS.map(p => {
        const active = selected.includes(p.id);
        return (
          <button key={p.id} type="button" onClick={() => onChange(active ? selected.filter(x => x !== p.id) : [...selected, p.id])}
            className={`p-2.5 rounded-xl border text-left transition-all ${active ? "border-[#D4B483] bg-[#D4B483]/10" : "border-[#27272A] bg-[#0A0A0A] hover:border-[#3F3F46]"}`}>
            <p className={`text-xs font-medium ${active ? "text-[#D4B483]" : "text-[#E8E8EA]"}`}>{p.label}</p>
            <p className="text-xs text-[#E8E8EA] mt-0.5">{p.description}</p>
          </button>
        );
      })}
    </div>
  );
}

export default function StaffManagement({ users, currentAdminId, onRefresh }) {
  const [createDialog, setCreateDialog] = useState(false);
  const [resetDialog, setResetDialog] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [form, setForm] = useState({ username: "", password: "", role: "staff" });
  const [selectedPerms, setSelectedPerms] = useState(["orders", "menu", "inventory"]);
  const [saving, setSaving] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [permDialog, setPermDialog] = useState(false);
  const [editPerms, setEditPerms] = useState([]);

  const handleCreate = async () => {
    if (!form.username || !form.password) { toast.error("Fyll i alla fält"); return; }
    setSaving(true);
    try {
      await api.post("/admin/staff", { ...form, permissions: form.role === "admin" ? STAFF_PERMISSIONS.map(p => p.id) : selectedPerms });
      toast.success(`Användare '${form.username}' skapad`);
      setCreateDialog(false);
      setForm({ username: "", password: "", role: "staff" });
      setSelectedPerms(["orders", "menu", "inventory"]);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte skapa användare");
    } finally { setSaving(false); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) { toast.error("Lösenordet måste vara minst 6 tecken"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/staff/${resetUser.id}/reset-password`, { new_password: newPassword });
      toast.success(`Lösenord återställt för '${resetUser.username}'`);
      setResetDialog(false);
      setResetUser(null);
      setNewPassword("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte återställa lösenord");
    } finally { setSaving(false); }
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`Radera användare '${username}'?`)) return;
    try {
      await api.delete(`/admin/staff/${userId}`);
      toast.success(`Användare '${username}' raderad`);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte radera");
    }
  };

  const handleEditPermissions = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/staff/${permUser.id}/permissions`, { permissions: editPerms });
      toast.success(`Behörigheter uppdaterade för '${permUser.username}'`);
      setPermDialog(false);
      setPermUser(null);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte uppdatera behörigheter");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="staff-management">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-playfair text-xl font-bold text-[#EDEDED]">Personalhantering</h2>
          <p className="text-[#E8E8EA] text-xs mt-1">Hantera admin- och personalkonton</p>
        </div>
        <Button onClick={() => setCreateDialog(true)} className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold" data-testid="add-staff-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Lägg till användare
        </Button>
      </div>

      <div className="space-y-3">
        {users.map(user => (
          <div key={user.id} className="bg-[#121212] rounded-xl border border-[#27272A] p-4 flex items-center justify-between group hover:border-[#3F3F46] transition-colors" data-testid={`staff-user-${user.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${user.role === "admin" ? "bg-[#D4B483]/15" : "bg-[#3B82F6]/15"}`}>
                {user.role === "admin" ? <Shield className="w-5 h-5 text-[#D4B483]" /> : <UserCog className="w-5 h-5 text-[#3B82F6]" />}
              </div>
              <div className="min-w-0">
                <p className="text-[#EDEDED] font-medium text-sm truncate">{user.username}</p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <Badge className={`text-xs border-0 ${user.role === "admin" ? "bg-[#D4B483]/15 text-[#D4B483]" : "bg-[#3B82F6]/15 text-[#3B82F6]"}`}>
                    {user.role}
                  </Badge>
                  {user.role === "staff" && user.permissions && user.permissions.length > 0 && (
                    <span className="text-xs text-[#E8E8EA]">{user.permissions.length} permissions</span>
                  )}
                  <span className="text-xs text-[#E8E8EA] hidden sm:inline">
                    Created {new Date(user.created_at).toLocaleDateString("sv-SE")}
                  </span>
                </div>
                {user.role === "staff" && user.permissions && (
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {user.permissions.slice(0, 4).map(pid => {
                      const p = STAFF_PERMISSIONS.find(x => x.id === pid);
                      return p ? <span key={pid} className="text-[9px] bg-[#1A1A1A] text-[#E8E8EA] px-1.5 py-0.5 rounded">{p.label}</span> : null;
                    })}
                    {user.permissions.length > 4 && (
                      <span className="text-[9px] text-[#E8E8EA]">+{user.permissions.length - 4}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all">
              {user.role === "staff" && (
                <Button variant="ghost" size="sm" onClick={() => { setPermUser(user); setEditPerms(user.permissions || []); setPermDialog(true); }}
                  className="text-[#E8E8EA] hover:text-[#D4B483]" title="Redigera behörigheter">
                  <Shield className="w-4 h-4" />
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => { setResetUser(user); setResetDialog(true); }}
                className="text-[#E8E8EA] hover:text-[#F59E0B]" title="Återställ lösenord">
                <Key className="w-4 h-4" />
              </Button>
              {user.id !== currentAdminId && (
                <Button variant="ghost" size="sm" onClick={() => handleDelete(user.id, user.username)}
                  className="text-[#E8E8EA] hover:text-[#EF4444]" data-testid={`delete-staff-${user.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="bg-[#121212] border-[#27272A] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#EDEDED] font-playfair">Lägg till personal</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Användarnamn</Label>
              <Input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                placeholder="t.ex. anna, kitchen_1" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11" data-testid="staff-username-input" />
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Lösenord</Label>
              <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Minst 6 tecken" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11" data-testid="staff-password-input" />
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Roll</Label>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => { setForm(f => ({ ...f, role: "staff" })); setSelectedPerms(["orders", "menu", "inventory"]); }}
                  className={`p-3 rounded-xl border text-center transition-all ${form.role === "staff" ? "border-[#3B82F6] bg-[#3B82F6]/10" : "border-[#27272A] bg-[#0A0A0A]"}`}
                  data-testid="role-staff-btn">
                  <UserCog className={`w-5 h-5 mx-auto mb-1 ${form.role === "staff" ? "text-[#3B82F6]" : "text-[#E8E8EA]"}`} />
                  <span className={`text-xs font-medium ${form.role === "staff" ? "text-[#3B82F6]" : "text-[#E8E8EA]"}`}>Personal</span>
                  <p className="text-xs text-[#B4B4BC] mt-0.5">Anpassade behörigheter</p>
                </button>
                <button onClick={() => setForm(f => ({ ...f, role: "admin" }))}
                  className={`p-3 rounded-xl border text-center transition-all ${form.role === "admin" ? "border-[#D4B483] bg-[#D4B483]/10" : "border-[#27272A] bg-[#0A0A0A]"}`}
                  data-testid="role-admin-btn">
                  <Shield className={`w-5 h-5 mx-auto mb-1 ${form.role === "admin" ? "text-[#D4B483]" : "text-[#E8E8EA]"}`} />
                  <span className={`text-xs font-medium ${form.role === "admin" ? "text-[#D4B483]" : "text-[#E8E8EA]"}`}>Admin</span>
                  <p className="text-xs text-[#B4B4BC] mt-0.5">Full åtkomst</p>
                </button>
              </div>
            </div>
            {form.role === "staff" && (
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Behörigheter</Label>
                <PermissionGrid selected={selectedPerms} onChange={setSelectedPerms} />
              </div>
            )}
            <Button onClick={handleCreate} disabled={saving} className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] h-11 font-semibold" data-testid="save-staff-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create User"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={resetDialog} onOpenChange={setResetDialog}>
        <DialogContent className="bg-[#121212] border-[#27272A]">
          <DialogHeader><DialogTitle className="text-[#EDEDED] font-playfair">Återställ lösenord</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-[#E8E8EA] text-sm">Återställ lösenord för <span className="font-semibold text-[#EDEDED]">{resetUser?.username}</span></p>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Nytt lösenord</Label>
              <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                placeholder="Minst 6 tecken" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11" />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setResetDialog(false)} className="flex-1 border-[#27272A] text-[#EDEDED] hover:bg-[#1A1A1A]">
                Avbryt
              </Button>
              <Button onClick={handleResetPassword} disabled={saving} className="flex-1 bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Återställ lösenord"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={permDialog} onOpenChange={setPermDialog}>
        <DialogContent className="bg-[#121212] border-[#27272A]">
          <DialogHeader><DialogTitle className="text-[#EDEDED] font-playfair">Redigera behörigheter</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-[#E8E8EA] text-sm">Hantera behörigheter för <span className="font-semibold text-[#EDEDED]">{permUser?.username}</span></p>
            <PermissionGrid selected={editPerms} onChange={setEditPerms} />
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setPermDialog(false)} className="flex-1 border-[#27272A] text-[#EDEDED] hover:bg-[#1A1A1A]">
                Avbryt
              </Button>
              <Button onClick={handleEditPermissions} disabled={saving} className="flex-1 bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Permissions"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
